// Travel Rule UX copy. Keyed by (entity, scope). All scopes Workstream A
// uses (`thirdPartyBlock`, `thirdPartyBlockBackfill`) are preserved as-is;
// new scopes added by Workstream B (`pendingReview*`, `actionRequired*`,
// `infoRequest*`, `slaPendingReview`, `vaspMismatchWarning`) sit alongside.

export type Entity = "AU" | "US";

export type CopyScope =
  | "thirdPartyBlock"
  | "thirdPartyBlockBackfill"
  | "rejectionRegulatory"
  | "rejectionThirdParty"
  | "pendingReview"
  | "pendingReviewDeposit"
  | "actionRequired"
  | "actionRequiredDeposit"
  | "infoRequest"
  | "vaspMismatchWarning"
  | "slaPendingReview";

export interface CopyEntry {
  title: string;
  body: string;
  primaryCta?: string;
  secondaryCta?: string;
}

const sharedThirdPartyBlock: CopyEntry = {
  title: "We can't process this transfer",
  body:
    "To comply with financial regulations, Caleb & Brown can only permit transfers to an account or wallet you own. " +
    "If this address belongs to you, please reach out to your broker so we can update your records.",
  primaryCta: "Use a different address",
  secondaryCta: "Cancel",
};

const sharedInfoRequest: CopyEntry = {
  title: "Counterparty has requested more information",
  body:
    "The receiving exchange has asked for additional information about this transfer. " +
    "Please complete the form below — your funds remain on hold until they confirm receipt.",
  primaryCta: "Provide info",
  secondaryCta: "Cancel withdrawal",
};

const sharedVaspMismatch: CopyEntry = {
  title: "Recipient details don't match",
  body:
    "The exchange you selected for this address doesn't match the one we resolved on-chain. " +
    "Acknowledge below to continue — your transfer will be reviewed by our compliance team " +
    "before it's released.",
  primaryCta: "I understand",
};

const sharedSla = (n: number): CopyEntry => ({
  title: "Your transfer is being reviewed",
  body: `Caleb & Brown reviews every Travel Rule transfer before release. We'll let you know within ${n} business days.`,
});

export const copy: Record<Entity, Record<CopyScope, CopyEntry>> = {
  AU: {
    thirdPartyBlock: sharedThirdPartyBlock,
    thirdPartyBlockBackfill: {
      ...sharedThirdPartyBlock,
      body:
        "Caleb & Brown is required by AUSTRAC to limit cryptocurrency transfers to accounts you own. " +
        "If this address belongs to you, please contact your broker so we can update your records.",
    },
    rejectionRegulatory: {
      title: "Withdrawal rejected",
      body: "This transfer was rejected following our regulatory review. Please contact support.",
      primaryCta: "Contact support",
    },
    rejectionThirdParty: {
      title: "Withdrawal rejected",
      body: "We can only permit transfers to an account or wallet you own. Please contact support.",
      primaryCta: "Contact support",
    },
    pendingReview: {
      title: "Your withdrawal is being reviewed",
      body: "We will let you know as soon as it's been reviewed.",
    },
    pendingReviewDeposit: {
      title: "Your deposit is being reviewed",
      body: "Your funds will be credited once cleared. Do not send another transaction.",
    },
    actionRequired: {
      title: "We need a little more information",
      body: "Please provide the missing information so we can release your transfer.",
      primaryCta: "Provide info",
    },
    actionRequiredDeposit: {
      title: "More information needed for your deposit",
      body:
        "We need a little more information about this incoming deposit before we can credit it. " +
        "Provide it below to release your funds.",
      primaryCta: "Provide info",
    },
    infoRequest: sharedInfoRequest,
    vaspMismatchWarning: sharedVaspMismatch,
    slaPendingReview: sharedSla(3),
  },
  US: {
    thirdPartyBlock: sharedThirdPartyBlock,
    thirdPartyBlockBackfill: {
      ...sharedThirdPartyBlock,
      body:
        "Caleb & Brown is required by FinCEN to limit cryptocurrency transfers to accounts you own. " +
        "If this address belongs to you, please contact your broker so we can update your records.",
    },
    rejectionRegulatory: {
      title: "Withdrawal rejected",
      body: "This transfer was rejected following our regulatory review. Please contact support.",
      primaryCta: "Contact support",
    },
    rejectionThirdParty: {
      title: "Withdrawal rejected",
      body: "We can only permit transfers to an account or wallet you own. Please contact support.",
      primaryCta: "Contact support",
    },
    pendingReview: {
      title: "Your withdrawal is being reviewed",
      body: "We will let you know as soon as it's been reviewed.",
    },
    pendingReviewDeposit: {
      title: "Your deposit is being reviewed",
      body: "Your funds will be credited once cleared. Do not send another transaction.",
    },
    actionRequired: {
      title: "We need a little more information",
      body: "Please provide the missing information so we can release your transfer.",
      primaryCta: "Provide info",
    },
    actionRequiredDeposit: {
      title: "More information needed for your deposit",
      body:
        "We need a little more information about this incoming deposit before we can credit it. " +
        "Provide it below to release your funds.",
      primaryCta: "Provide info",
    },
    infoRequest: sharedInfoRequest,
    vaspMismatchWarning: sharedVaspMismatch,
    slaPendingReview: sharedSla(3),
  },
};

/**
 * Convenience accessor so call sites can `getCopy(entity, "thirdPartyBlock")`
 * without worrying about undefined keys.
 */
export function getCopy(entity: Entity | undefined, scope: CopyScope): CopyEntry {
  return copy[entity ?? "AU"][scope];
}

/**
 * Build the SLA copy with a config-driven business-day count. Falls back to
 * the static copy when N is missing/invalid.
 */
export function getSlaCopy(
  entity: Entity | undefined,
  pendingReviewBusinessDays: number | undefined,
): CopyEntry {
  const base = getCopy(entity, "slaPendingReview");
  if (!pendingReviewBusinessDays || pendingReviewBusinessDays <= 0) return base;
  return {
    ...base,
    body: `Caleb & Brown reviews every Travel Rule transfer before release. We'll let you know within ${pendingReviewBusinessDays} business days.`,
  };
}
